import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, X, Check, Gift, Info } from "lucide-react";
import { buildAdminApi } from "../../lib/admin";
import { toast } from "sonner";

const EMOJI_CHOICES = ["🫘", "🌱", "☕", "🍵", "🥤", "🍪", "👜", "🎁", "🧁", "🥐", "🍫", "👕"];

const AdminCatalog = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [items, setItems] = useState([]);
    const [limit, setLimit] = useState(7);
    const [activeCount, setActiveCount] = useState(0);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminApi
            .get("/admin/catalog")
            .then((r) => {
                setItems(r.data.items || []);
                setLimit(r.data.limit || 7);
                setActiveCount(r.data.active_count || 0);
            })
            .catch(() => toast.error("Couldn't load catalog"))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [slug]);

    const save = async (form) => {
        try {
            if (editing === "new") {
                await adminApi.post("/admin/catalog", form);
                toast.success("Reward added");
            } else {
                await adminApi.patch(`/admin/catalog/${editing.id}`, form);
                toast.success("Saved");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const toggleActive = async (item) => {
        try {
            await adminApi.patch(`/admin/catalog/${item.id}`, { active: !item.active });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not toggle");
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete "${item.name}"?`)) return;
        try {
            await adminApi.delete(`/admin/catalog/${item.id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const canAdd = activeCount < limit;

    return (
        <div>
            <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                <h1 className="font-display font-black text-3xl">Rewards catalog</h1>
                <button
                    data-testid="add-catalog-item-btn"
                    disabled={!canAdd}
                    onClick={() => setEditing("new")}
                    className={`rounded-full px-5 py-2 font-display font-bold text-sm active:scale-95 flex items-center gap-2 ${
                        canAdd ? "bg-[#FF4F18] text-white" : "bg-[#E8DCCB] text-[#8C7A72] cursor-not-allowed"
                    }`}
                >
                    <Plus className="h-4 w-4" strokeWidth={2.5} /> Add reward
                </button>
            </div>
            <p className="text-sm text-[#8C7A72] max-w-xl">
                Up to <strong>{limit} active rewards</strong>. Customers spend <strong>Beans</strong> earned from
                coffee purchases (7 Beans per coffee) to redeem items at your counter.
            </p>
            <div
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-[#E8DCCB] px-3 py-1.5 text-xs"
                data-testid="catalog-count-badge"
            >
                <Gift className="h-3.5 w-3.5 text-[#FF4F18]" strokeWidth={2.5} />
                <span className="font-display font-bold">
                    {activeCount} / {limit} active
                </span>
            </div>

            {loading ? (
                <div className="mt-10 text-[#8C7A72]">Loading…</div>
            ) : items.length === 0 ? (
                <div className="mt-10 rounded-3xl bg-white border border-[#E8DCCB] p-10 text-center">
                    <Gift className="h-8 w-8 text-[#FF4F18] mx-auto" strokeWidth={2} />
                    <div className="mt-4 font-display font-bold text-lg">No rewards yet</div>
                    <p className="mt-1 text-sm text-[#8C7A72] max-w-md mx-auto">
                        Add up to 7 items — house-roasted beans, mugs, merch, or a Black Coffee redemption.
                    </p>
                    <button
                        onClick={() => setEditing("new")}
                        className="mt-6 rounded-full bg-[#FF4F18] text-white px-6 py-3 font-display font-bold active:scale-95 inline-flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" strokeWidth={2.5} /> Add your first reward
                    </button>
                </div>
            ) : (
                <div className="mt-6 grid gap-3">
                    {items.map((it) => (
                        <div
                            key={it.id}
                            data-testid={`catalog-row-${it.id}`}
                            className={`rounded-2xl bg-white border p-4 flex items-center gap-4 ${
                                it.active ? "border-[#E8DCCB]" : "border-[#E8DCCB]/50 opacity-60"
                            }`}
                        >
                            <div className="h-14 w-14 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center text-3xl shrink-0">
                                {it.emoji || "🎁"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-black text-base truncate">{it.name}</div>
                                {it.description && (
                                    <div className="text-xs text-[#8C7A72] truncate">{it.description}</div>
                                )}
                                <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-display font-bold text-[#FF4F18]">
                                    <span className="text-sm">🫘</span> {it.cost_beans} beans
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    data-testid={`catalog-toggle-${it.id}`}
                                    onClick={() => toggleActive(it)}
                                    className={`text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full ${
                                        it.active
                                            ? "bg-[#1B995E]/10 text-[#1B995E]"
                                            : "bg-[#8C7A72]/10 text-[#8C7A72]"
                                    }`}
                                >
                                    {it.active ? "Active" : "Hidden"}
                                </button>
                                <button
                                    data-testid={`catalog-edit-${it.id}`}
                                    onClick={() => setEditing(it)}
                                    className="text-xs font-display font-bold px-3 py-1.5 rounded-full bg-[#2C1A12] text-white active:scale-95"
                                >
                                    Edit
                                </button>
                                <button
                                    data-testid={`catalog-delete-${it.id}`}
                                    onClick={() => remove(it)}
                                    className="h-8 w-8 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center hover:border-[#FF4F18] hover:text-[#FF4F18]"
                                >
                                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-8 rounded-2xl bg-white border border-[#E8DCCB] p-4 flex items-start gap-3">
                <Info className="h-4 w-4 text-[#FF4F18] shrink-0 mt-0.5" strokeWidth={2.5} />
                <div className="text-xs text-[#8C7A72] leading-relaxed">
                    <strong className="text-[#2C1A12]">How it works:</strong> Customers earn 7 Beans per coffee item on
                    every paid order. They redeem items on the <span className="font-mono">/rewards</span> page — your
                    staff sees each redemption ticket in the <strong>Staff board</strong> and hands over the item at
                    the counter.
                </div>
            </div>

            <AnimatePresence>
                {editing && (
                    <EditModal
                        item={editing === "new" ? null : editing}
                        onClose={() => setEditing(null)}
                        onSave={save}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const EditModal = ({ item, onClose, onSave }) => {
    const [name, setName] = useState(item?.name || "");
    const [description, setDescription] = useState(item?.description || "");
    const [costBeans, setCostBeans] = useState(item?.cost_beans || 70);
    const [emoji, setEmoji] = useState(item?.emoji || "🎁");
    const [active, setActive] = useState(item?.active ?? true);
    const [saving, setSaving] = useState(false);

    const valid = name.trim().length > 0 && Number(costBeans) >= 1;

    const submit = async () => {
        if (!valid || saving) return;
        setSaving(true);
        await onSave({
            name: name.trim(),
            description: description.trim(),
            cost_beans: parseInt(costBeans, 10),
            emoji,
            active,
        });
        setSaving(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#2C1A12]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
            <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 40, opacity: 0 }}
                className="w-full sm:max-w-lg bg-[#F9F6F0] rounded-t-3xl sm:rounded-3xl p-6"
                data-testid="catalog-edit-modal"
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-black text-2xl">
                        {item ? "Edit reward" : "New reward"}
                    </h2>
                    <button
                        data-testid="catalog-modal-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                    >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">Icon</div>
                        <div className="mt-2 flex flex-wrap gap-2" data-testid="catalog-emoji-picker">
                            {EMOJI_CHOICES.map((e) => (
                                <button
                                    key={e}
                                    onClick={() => setEmoji(e)}
                                    className={`h-11 w-11 rounded-2xl text-2xl flex items-center justify-center border-2 ${
                                        emoji === e ? "bg-[#FF4F18]/10 border-[#FF4F18]" : "bg-white border-[#E8DCCB]"
                                    }`}
                                >
                                    {e}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">Name</div>
                        <input
                            data-testid="catalog-name-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. House Roast 250g"
                            className="mt-2 w-full px-4 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] text-base"
                        />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">Description</div>
                        <input
                            data-testid="catalog-desc-input"
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Short one-liner for customers"
                            className="mt-2 w-full px-4 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] text-base"
                        />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">Bean cost</div>
                        <div className="mt-2 relative">
                            <input
                                data-testid="catalog-cost-input"
                                type="number"
                                min="1"
                                value={costBeans}
                                onChange={(e) => setCostBeans(e.target.value)}
                                className="w-full pl-4 pr-24 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] text-base font-display font-bold"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#8C7A72]">
                                beans
                            </span>
                        </div>
                        <p className="mt-1 text-xs text-[#8C7A72]">
                            Guide: 70 = medium coffee · 350 = 250g beans · 700 = mug
                        </p>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            data-testid="catalog-active-toggle"
                            type="checkbox"
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            className="h-5 w-5 accent-[#FF4F18]"
                        />
                        <span className="text-sm font-display font-bold">
                            Active — visible to customers
                        </span>
                    </label>
                </div>

                <div className="mt-6 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 rounded-full bg-white border-2 border-[#E8DCCB] font-display font-bold active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="catalog-save-btn"
                        onClick={submit}
                        disabled={!valid || saving}
                        className={`flex-[2] py-3 rounded-full font-display font-bold flex items-center justify-center gap-2 active:scale-95 ${
                            valid && !saving
                                ? "bg-[#FF4F18] text-white"
                                : "bg-[#E8DCCB] text-[#8C7A72] cursor-not-allowed"
                        }`}
                    >
                        <Check className="h-4 w-4" strokeWidth={2.5} /> {saving ? "Saving…" : "Save reward"}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default AdminCatalog;
